import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ArrowUpRight, ArrowDownRight, TrendingUp, AlertTriangle, CalendarDays, Landmark, Check } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Cell, Line, ComposedChart, Area } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
const fmt = (v) => v == null ? "-" : new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 }).format(v);
const fmtEur = (v) => v == null ? "-" : new Intl.NumberFormat("it-IT", { style:"currency", currency:"EUR", maximumFractionDigits:0 }).format(v);

export const CashFlowPage = ({ clientId, parentYear }) => {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(parentYear || new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [rateizza, setRateizza] = useState(false);
  // Persist the F24 toggle PER CLIENT so the user doesn't have to flip it
  // every visit (e.g. for clients where the agent doesn't do tax planning).
  const f24StorageKey = `cashflow:include_f24:${clientId}`;
  const [includeF24, setIncludeF24] = useState(() => {
    const saved = localStorage.getItem(f24StorageKey);
    return saved === null ? true : saved === 'true';
  });
  useEffect(() => {
    localStorage.setItem(f24StorageKey, String(includeF24));
  }, [includeF24, f24StorageKey]);
  const [initialBalance, setInitialBalance] = useState('');
  const [initialBalanceSaved, setInitialBalanceSaved] = useState(null);
  const [savingBalance, setSavingBalance] = useState(false);
  const [showBalanceInput, setShowBalanceInput] = useState(false);
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (parentYear && parentYear !== year) setYear(parentYear);
  }, [parentYear]);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/clients/${clientId}/cashflow`, { params: { year, rateizza }, headers });
        setData(res.data);
        const ib = res.data.initial_balance;
        setInitialBalanceSaved(ib);
        setInitialBalance(ib != null ? ib.toString() : '');
      } catch (e) {
        setData(null);
      } finally { setLoading(false); }
    };
    fetch();
  }, [clientId, year, rateizza]);

  const saveInitialBalance = async () => {
    setSavingBalance(true);
    try {
      const amount = initialBalance.trim() === '' ? null : parseFloat(initialBalance);
      await axios.post(`${API}/clients/${clientId}/initial-balance`, { year, amount }, { headers });
      setInitialBalanceSaved(amount);
      toast.success(amount != null ? `Saldo iniziale ${year} salvato` : `Saldo iniziale ${year} rimosso`);
      // Refresh cashflow data
      const res = await axios.get(`${API}/clients/${clientId}/cashflow`, { params: { year, rateizza }, headers });
      setData(res.data);
      setShowBalanceInput(false);
    } catch { toast.error("Errore salvataggio"); }
    finally { setSavingBalance(false); }
  };

  if (loading) return <div className="py-12 text-center text-muted-foreground">Caricamento flussi di cassa...</div>;
  if (!data || !data.months.some(m => m.has_data)) return (
    <div className="py-12 text-center text-muted-foreground">
      <p className="text-lg font-semibold mb-1">Nessun dato per il {year}</p>
      <p>Carica i bilanci per vedere l'analisi dei flussi di cassa.</p>
    </div>
  );

  const { months, commitment_detail, has_owner_compensation, f24_detail, f24_tax_year } = data;
  const dataMonths = months.filter(m => m.has_data);
  const projectedMonths = months.filter(m => !m.has_data && m.net_cashflow != null);

  // Totals from actual data
  const totalRevenue = dataMonths.reduce((s,m) => s + (m.revenue || 0), 0);
  const totalEbitda = dataMonths.reduce((s,m) => s + (m.ebitda || 0), 0);
  const totalNetCF = dataMonths.reduce((s,m) => s + (includeF24 ? (m.net_cashflow || 0) : ((m.net_cashflow || 0) + (m.f24 || 0))), 0);
  const totalCommitments = months.reduce((s,m) => s + (m.commitments || 0), 0);
  const totalOwnerComp = months.reduce((s,m) => s + (m.owner_compensation || 0), 0);
  const totalF24 = months.reduce((s,m) => s + (m.f24 || 0), 0);
  const hasF24 = totalF24 > 0;

  // Chart data - adjust for includeF24 toggle
  const chartData = months.map(m => {
    const f24Val = includeF24 ? (m.f24 || 0) : 0;
    const hasActual = m.has_data;
    const hasProjected = !m.has_data && m.revenue != null;

    let entrate = null, uscite = null, netto = null;
    if (hasActual) {
      entrate = m.ebitda;
      uscite = -(m.taxes + m.financial_charges + m.commitments + m.owner_compensation + f24Val);
      netto = m.ebitda - m.taxes - m.financial_charges - m.commitments - m.owner_compensation - f24Val;
    } else if (hasProjected) {
      entrate = m.ebitda;
      uscite = -((m.taxes || 0) + (m.financial_charges || 0) + m.commitments + m.owner_compensation + f24Val);
      netto = includeF24 ? m.net_cashflow : (m.net_cashflow + (m.f24 || 0));
    }

    return {
      name: MONTHS[m.month - 1],
      entrate,
      uscite,
      netto,
      saldo: includeF24 ? m.projected_balance : (m.projected_balance != null ? (m.projected_balance + months.slice(0, m.month).reduce((s, mm) => s + (mm.f24 || 0), 0)) : null),
      isProjected: !m.has_data,
      revenue: m.revenue,
    };
  });

  return (
    <div className="space-y-6" data-testid="cashflow-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-bold">Analisi Flussi di Cassa</h3>
          <p className="text-xs text-muted-foreground">{dataMonths.length} mesi con dati, {projectedMonths.length} mesi proiettati {projectedMonths.length > 0 ? '(da trend BEP)' : ''}{hasF24 && f24_tax_year ? ` — F24 su utile ${f24_tax_year}` : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          {hasF24 && (<>
            <Button
              variant={includeF24 ? "default" : "outline"}
              size="sm"
              onClick={() => setIncludeF24(!includeF24)}
              data-testid="include-f24-toggle"
              className={includeF24 ? "bg-blue-600 hover:bg-blue-700" : "border-blue-400 text-blue-700"}
            >
              {includeF24 ? "F24 Inclusi" : "F24 Esclusi"}
            </Button>
            <Button
              variant={rateizza ? "default" : "outline"}
              size="sm"
              onClick={() => setRateizza(!rateizza)}
              data-testid="rateizza-toggle"
              className={rateizza ? "bg-amber-600 hover:bg-amber-700" : "border-amber-400 text-amber-700"}
              disabled={!includeF24}
            >
              <CalendarDays className="h-4 w-4 mr-1" />
              {rateizza ? "Rateizzazione ON" : "Rateizza F24 Luglio"}
            </Button>
          </>)}
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2024,2025,2026].map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Initial Balance Bar */}
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border text-sm">
        <Landmark className="h-4 w-4 text-slate-500 shrink-0" />
        <span className="text-muted-foreground shrink-0">Saldo banca inizio {year}:</span>
        {showBalanceInput ? (
          <>
            <Input
              type="number"
              className="w-36 h-7 text-sm"
              value={initialBalance}
              onChange={e => setInitialBalance(e.target.value)}
              placeholder="es. 15000"
              data-testid="initial-balance-input"
            />
            <span className="text-muted-foreground text-xs">EUR</span>
            <Button size="sm" variant="default" className="h-7 px-2" onClick={saveInitialBalance} disabled={savingBalance} data-testid="save-initial-balance-btn">
              <Check className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => { setShowBalanceInput(false); setInitialBalance(initialBalanceSaved != null ? initialBalanceSaved.toString() : ''); }}>
              Annulla
            </Button>
          </>
        ) : (
          <>
            <span className={`font-semibold tabular-nums ${initialBalanceSaved != null ? 'text-emerald-700' : 'text-muted-foreground'}`} data-testid="initial-balance-display">
              {initialBalanceSaved != null ? fmtEur(initialBalanceSaved) : 'Non impostato'}
            </span>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-blue-600 hover:text-blue-800" onClick={() => setShowBalanceInput(true)} data-testid="edit-initial-balance-btn">
              {initialBalanceSaved != null ? 'Modifica' : 'Imposta'}
            </Button>
            {initialBalanceSaved == null && (
              <span className="text-[10px] text-muted-foreground">Imposta il saldo per una proiezione piu accurata</span>
            )}
          </>
        )}
      </div>

      {/* Summary Cards */}
      <div className={`grid grid-cols-2 ${hasF24 ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-3`}>
        <Card className="border-l-4 border-l-emerald-400">
          <CardContent className="pt-3 pb-2">
            <div className="text-[10px] text-muted-foreground uppercase">EBITDA Annuo</div>
            <div className="text-xl font-bold text-emerald-700 tabular-nums">{fmtEur(totalEbitda)}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-400">
          <CardContent className="pt-3 pb-2">
            <div className="text-[10px] text-muted-foreground uppercase">Rate + Impegni</div>
            <div className="text-xl font-bold text-red-700 tabular-nums">{fmtEur(totalCommitments)}</div>
          </CardContent>
        </Card>
        {hasF24 && (
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="pt-3 pb-2">
              <div className="text-[10px] text-muted-foreground uppercase">F24 Imposte{f24_tax_year ? ` (utile ${f24_tax_year})` : ''}</div>
              <div className="text-xl font-bold text-amber-700 tabular-nums" data-testid="total-f24">{fmtEur(totalF24)}</div>
              <div className="text-[10px] text-muted-foreground">
                {(f24_detail || []).map((f, i) => <span key={i}>{i > 0 ? ' + ' : ''}{MONTHS[f.month - 1]}: {fmtEur(f.amount)}</span>)}
              </div>
            </CardContent>
          </Card>
        )}
        <Card className={`border-l-4 ${totalNetCF >= 0 ? 'border-l-blue-400' : 'border-l-orange-400'}`}>
          <CardContent className="pt-3 pb-2">
            <div className="text-[10px] text-muted-foreground uppercase">Cash Flow Netto</div>
            <div className={`text-xl font-bold tabular-nums ${totalNetCF >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>{fmtEur(totalNetCF)}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-slate-400">
          <CardContent className="pt-3 pb-2">
            <div className="text-[10px] text-muted-foreground uppercase">Saldo Proiettato Dic</div>
            <div className="text-xl font-bold tabular-nums">{(() => {
              const dec = months[11];
              if (!dec || dec.projected_balance == null) return '-';
              const adj = includeF24 ? dec.projected_balance : (dec.projected_balance + months.reduce((s, mm) => s + (mm.f24 || 0), 0));
              return fmtEur(adj);
            })()}</div>
          </CardContent>
        </Card>
      </div>

      {/* Cash Flow Netto Chart */}
      <Card>
        <CardHeader className="pb-1">
          <CardTitle className="text-base">Cash Flow Netto Mensile</CardTitle>
          <CardDescription>Barre verdi = mesi positivi, barre rosse = mesi negativi. Colori chiari = proiezione.</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, bottom: 5, left: -5 }}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
              <Tooltip
                formatter={(v) => [fmtEur(v), 'Cash Flow Netto']}
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
              />
              <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
              <Bar dataKey="netto" name="netto" radius={[3,3,0,0]}>
                {chartData.map((d, i) => (
                  <Cell key={i} fill={d.netto >= 0 ? (d.isProjected ? '#86efac' : '#059669') : (d.isProjected ? '#fca5a5' : '#dc2626')}
                    opacity={d.isProjected ? 0.5 : 1} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Saldo Proiettato Chart */}
      <Card>
        <CardHeader className="pb-1">
          <CardTitle className="text-base">Saldo Proiettato Cumulativo</CardTitle>
          <CardDescription>Andamento del saldo di cassa nel corso dell'anno{initialBalanceSaved != null ? ` (partenza: ${fmtEur(initialBalanceSaved)})` : ''}.</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 10, bottom: 5, left: -5 }}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
              <Tooltip
                formatter={(v) => [fmtEur(v), 'Saldo Proiettato']}
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
              />
              <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
              <Area type="monotone" dataKey="saldo" stroke="#2563eb" strokeWidth={2.5} fill="#dbeafe" dot={{ r: 3, fill: '#2563eb' }} name="saldo" />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Monthly Detail Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Dettaglio Mensile</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] border-collapse">
              <thead>
                <tr className="bg-slate-100 border-b-2 border-slate-300">
                  <th className="sticky left-0 z-20 bg-slate-100 text-left px-3 py-2 min-w-[180px] border-r text-xs font-bold uppercase">Voce</th>
                  {MONTHS.map((m, i) => {
                    const md = months[i];
                    return (
                      <th key={i} className={`text-center px-2 py-2 min-w-[92px] border-r text-xs ${md?.has_data ? 'font-bold' : 'font-normal text-slate-400 italic'}`}>
                        {m}{!md?.has_data && md?.net_cashflow != null ? '*' : ''}
                      </th>
                    );
                  })}
                  <th className="text-center px-2 py-2 min-w-[100px] font-bold bg-slate-200 text-xs">Totale</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { key: 'ebitda', label: 'EBITDA', style: 'font-semibold text-emerald-800 bg-emerald-50' },
                  { key: 'taxes', label: '(-) Imposte CE', style: 'text-slate-600', negate: true },
                  { key: 'financial_charges', label: '(-) Oneri Finanziari', style: 'text-slate-600', negate: true },
                  { key: 'commitments', label: '(-) Rate e Impegni', style: 'text-red-700 font-medium', negate: true },
                  ...(has_owner_compensation ? [{ key: 'owner_compensation', label: '(-) Compenso Soci', style: 'text-violet-700', negate: true }] : []),
                  ...(hasF24 ? [{ key: 'f24', label: '(-) F24 Imposte', style: 'text-amber-700 font-medium bg-amber-50/50', negate: true }] : []),
                  { key: 'net_cashflow', label: 'CASH FLOW NETTO', style: 'font-bold border-t-2 border-slate-300 bg-slate-50', highlight: true },
                  { key: 'bank_balance', label: 'Saldo Banca (reale)', style: 'text-blue-700 bg-blue-50/50 italic' },
                  { key: 'projected_balance', label: 'Saldo Proiettato', style: 'font-semibold text-blue-900 bg-blue-50 border-t' },
                ].map(row => (
                  <tr key={row.key} className={`border-b border-slate-100 ${row.style}`} data-testid={`cf-row-${row.key}`}>
                    <td className={`sticky left-0 z-20 px-3 py-1.5 border-r whitespace-nowrap bg-white ${row.style}`}>{row.label}</td>
                    {MONTHS.map((_, i) => {
                      const md = months[i];
                      let val = md?.[row.key];
                      // Adjust net_cashflow and projected_balance when F24 is excluded
                      if (!includeF24) {
                        if (row.key === 'net_cashflow' && val != null) {
                          val = val + (md?.f24 || 0);
                        } else if (row.key === 'projected_balance' && val != null) {
                          val = val + months.slice(0, i + 1).reduce((s, mm) => s + (mm.f24 || 0), 0);
                        }
                      }
                      if (val == null) return <td key={i} className="px-1 py-1.5 text-right border-r text-slate-200 tabular-nums">-</td>;
                      const display = row.negate ? -val : val;
                      return (
                        <td key={i} className={`px-1.5 py-1.5 text-right border-r tabular-nums ${!md?.has_data && row.key !== 'commitments' && row.key !== 'bank_balance' && row.key !== 'projected_balance' && row.key !== 'owner_compensation' && row.key !== 'f24' ? 'italic text-slate-400' : ''} ${row.highlight && val < 0 ? 'text-red-600' : ''} ${row.key === 'f24' && val > 0 ? 'font-semibold' : ''}`}>
                          {fmt(display)}
                        </td>
                      );
                    })}
                    <td className="px-2 py-1.5 text-right tabular-nums font-bold bg-slate-50 border-l-2">
                      {row.key === 'bank_balance' || row.key === 'projected_balance' ? 
                        (() => {
                          const v = months[11]?.[row.key];
                          if (v == null) return '-';
                          const adj = (!includeF24 && row.key === 'projected_balance') ? v + months.reduce((s,m) => s + (m.f24 || 0), 0) : v;
                          return fmt(adj);
                        })() :
                        (() => {
                          const sum = months.reduce((s,m) => {
                            let v = m[row.key] || 0;
                            if (!includeF24 && row.key === 'net_cashflow') v += (m.f24 || 0);
                            return s + v;
                          }, 0);
                          return fmt(row.negate ? -sum : sum);
                        })()
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {projectedMonths.length > 0 && (
            <p className="text-xs text-muted-foreground mt-2 italic">* Mesi proiettati: basati sulla media dei mesi con dati reali.</p>
          )}
        </CardContent>
      </Card>

      {/* Commitments Breakdown */}
      {commitment_detail.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Dettaglio Impegni Finanziari</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {commitment_detail.map((c, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded border bg-white">
                  <div>
                    <div className="font-medium text-sm">{c.description}</div>
                    <div className="text-xs text-muted-foreground">{c.start_date} → {c.end_date || 'indefinito'}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-sm tabular-nums">{fmtEur(c.monthly_amount)}/mese</div>
                    {c.total_remaining > 0 && <div className="text-xs text-muted-foreground">Residuo: {fmtEur(c.total_remaining)}</div>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CashFlowPage;
